import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  Camera,
  Coins,
  Receipt,
  Search,
  ShieldAlert,
} from 'lucide-react';
import type { Snapshot } from '@t212/shared';
import { api } from '@/lib/api';
import { EmptyState } from '@/components/states/EmptyState';
import { fmtDateTime, fmtMoney, fmtSignedMoney } from '@/lib/format';
import { cn } from '@/lib/cn';

type EventKind = 'order' | 'dividend' | 'transaction' | 'snapshot' | 'violation';
const ALL_KINDS: EventKind[] = ['order', 'dividend', 'transaction', 'snapshot', 'violation'];
const DEFAULT_KINDS: EventKind[] = ['order', 'dividend', 'transaction', 'violation']; // snapshots OFF by default
const KIND_LABEL: Record<EventKind, string> = {
  order: 'Orders',
  dividend: 'Dividends',
  transaction: 'Cash',
  snapshot: 'Snapshots',
  violation: 'Violations',
};

export const Route = createFileRoute('/activity')({
  component: ActivityPage,
});

type Event =
  | { kind: 'order'; ts: number; ticker: string; side: 'BUY' | 'SELL'; qty: number; price?: number; result?: number }
  | { kind: 'dividend'; ts: number; ticker: string; amount: number; currency: string }
  | { kind: 'transaction'; ts: number; type: string; amount: number; currency: string; ref?: string }
  | { kind: 'snapshot'; ts: number; total: number; positions: number; pnl: number }
  | { kind: 'violation'; ts: number; ruleId: string; message: string };

function ActivityPage() {
  const orders = useQuery({ queryKey: ['history-orders'], queryFn: () => api.orderHistory() });
  const divs = useQuery({ queryKey: ['history-divs'], queryFn: () => api.dividendHistory() });
  const tx = useQuery({ queryKey: ['history-tx'], queryFn: () => api.transactionHistory() });
  const snaps = useQuery({ queryKey: ['snapshots'], queryFn: api.snapshots });

  const [activeKinds, setActiveKinds] = useState<Set<EventKind>>(
    () => new Set(DEFAULT_KINDS),
  );
  const [tickerFilter, setTickerFilter] = useState('');

  function toggleKind(k: EventKind) {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  const events = useMemo<Event[]>(() => {
    const out: Event[] = [];
    for (const o of orders.data?.items ?? []) {
      out.push({
        kind: 'order',
        ts: new Date(o.filledAt ?? o.createdAt).getTime(),
        ticker: o.ticker,
        side: o.side,
        qty: o.filledQuantity || o.quantity,
        ...(o.fillPrice !== undefined ? { price: o.fillPrice } : {}),
        ...(o.result !== undefined ? { result: o.result } : {}),
      });
    }
    for (const d of divs.data?.items ?? []) {
      out.push({
        kind: 'dividend',
        ts: new Date(d.paidAt).getTime(),
        ticker: d.ticker,
        amount: d.amount,
        currency: d.currency,
      });
    }
    for (const t of tx.data?.items ?? []) {
      out.push({
        kind: 'transaction',
        ts: new Date(t.createdAt).getTime(),
        type: t.type,
        amount: t.amount,
        currency: t.currency,
        ...(t.reference ? { ref: t.reference } : {}),
      });
    }
    // Snapshots: show only one per day at most to avoid drowning the feed
    const dayMap = new Map<string, Snapshot>();
    for (const s of snaps.data?.snapshots ?? []) {
      const day = s.takenAt.slice(0, 10);
      const prev = dayMap.get(day);
      if (!prev || new Date(s.takenAt).getTime() > new Date(prev.takenAt).getTime()) {
        dayMap.set(day, s);
      }
    }
    for (const s of dayMap.values()) {
      out.push({
        kind: 'snapshot',
        ts: new Date(s.takenAt).getTime(),
        total: s.totalValue,
        positions: s.positionCount,
        pnl: s.unrealizedPnl,
      });
    }
    return out.sort((a, b) => b.ts - a.ts);
  }, [orders.data, divs.data, tx.data, snaps.data]);

  const filteredEvents = useMemo(() => {
    const t = tickerFilter.trim().toUpperCase();
    return events.filter((e) => {
      if (!activeKinds.has(e.kind)) return false;
      if (t.length === 0) return true;
      if (e.kind === 'order' || e.kind === 'dividend') {
        return e.ticker.toUpperCase().includes(t);
      }
      // Cash transactions, snapshots and violations don't have a ticker;
      // ticker filter excludes them when set.
      return false;
    });
  }, [events, activeKinds, tickerFilter]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 tracking-tight">Activity</h1>
        <p className="text-[12px] text-zinc-400 mt-0.5">
          Every fill, dividend, cash event and snapshot in one stream.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {ALL_KINDS.map((k) => {
          const on = activeKinds.has(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              aria-pressed={on}
              className={cn(
                'text-[11px] px-2 py-1 rounded-md border transition-colors',
                on
                  ? 'border-indigo-500/40 bg-indigo-500/15 text-indigo-100'
                  : 'border-zinc-800 text-zinc-400 hover:text-zinc-200',
              )}
            >
              {KIND_LABEL[k]}
            </button>
          );
        })}
        <div className="flex items-center gap-1.5 ml-auto px-2 py-1 rounded-md border border-zinc-800 bg-zinc-950">
          <Search className="size-3.5 text-zinc-400" />
          <input
            value={tickerFilter}
            onChange={(e) => setTickerFilter(e.target.value)}
            placeholder="Filter ticker"
            className="bg-transparent outline-none text-[11px] w-24 placeholder:text-zinc-400 font-mono"
          />
        </div>
      </div>

      <ul className="space-y-1">
        {filteredEvents.map((e, i) => (
          <EventRow key={i} event={e} />
        ))}
        {filteredEvents.length === 0 && (
          <EmptyState
            title={events.length === 0 ? 'No activity yet' : 'No events match your filters'}
            hint={
              events.length === 0
                ? undefined
                : 'Toggle event types or clear the ticker filter.'
            }
          />
        )}
      </ul>
    </motion.div>
  );
}

function EventRow({ event }: { event: Event }) {
  const ts = fmtDateTime(new Date(event.ts).toISOString());

  if (event.kind === 'order') {
    const Icon = event.side === 'BUY' ? ArrowDownLeft : ArrowUpRight;
    return (
      <Row time={ts}>
        <Icon
          className={cn(
            'size-4 mt-0.5',
            event.side === 'BUY' ? 'text-emerald-400' : 'text-rose-400',
          )}
        />
        <div className="flex-1 min-w-0">
          <Link
            to="/positions/$ticker"
            params={{ ticker: event.ticker }}
            className="hover:opacity-90"
          >
            <span className="text-zinc-200 text-sm">
              {event.side === 'BUY' ? 'Bought' : 'Sold'}{' '}
              <span className="font-mono">{event.ticker.split('_')[0]}</span>{' '}
              <span className="text-zinc-400">× {Math.abs(event.qty)}</span>
              {event.price !== undefined && (
                <span className="text-zinc-400"> @ {event.price.toFixed(2)}</span>
              )}
            </span>
          </Link>
        </div>
        {event.result !== undefined && (
          <span
            className={cn(
              'text-xs num',
              event.result >= 0 ? 'text-emerald-400' : 'text-rose-400',
            )}
          >
            {fmtSignedMoney(event.result)}
          </span>
        )}
      </Row>
    );
  }

  if (event.kind === 'dividend') {
    return (
      <Row time={ts}>
        <Coins className="size-4 mt-0.5 text-emerald-400" />
        <div className="flex-1 min-w-0">
          <span className="text-zinc-200 text-sm">
            Dividend from <span className="font-mono">{event.ticker.split('_')[0]}</span>
          </span>
        </div>
        <span className="text-xs num text-emerald-300">
          {fmtMoney(event.amount, event.currency)}
        </span>
      </Row>
    );
  }

  if (event.kind === 'transaction') {
    const Icon = event.amount >= 0 ? Banknote : Receipt;
    return (
      <Row time={ts}>
        <Icon className={cn('size-4 mt-0.5', event.amount >= 0 ? 'text-indigo-300' : 'text-zinc-400')} />
        <div className="flex-1 min-w-0">
          <span className="text-zinc-200 text-sm capitalize">
            {event.type.toLowerCase()}
            {event.ref && <span className="text-zinc-400"> · {event.ref}</span>}
          </span>
        </div>
        <span
          className={cn(
            'text-xs num',
            event.amount >= 0 ? 'text-emerald-300' : 'text-zinc-300',
          )}
        >
          {fmtSignedMoney(event.amount, event.currency)}
        </span>
      </Row>
    );
  }

  if (event.kind === 'snapshot') {
    return (
      <Row time={ts} muted>
        <Camera className="size-3.5 mt-0.5 text-zinc-500" />
        <div className="flex-1 min-w-0">
          <span className="text-zinc-500 text-xs">
            Snapshot · {event.positions} positions
          </span>
        </div>
        <span className="text-[11px] num text-zinc-500">{fmtMoney(event.total)}</span>
      </Row>
    );
  }

  if (event.kind === 'violation') {
    return (
      <Row time={ts}>
        <ShieldAlert className="size-4 mt-0.5 text-rose-400" />
        <div className="flex-1 min-w-0 text-sm text-zinc-300">
          {event.ruleId} — {event.message}
        </div>
      </Row>
    );
  }

  return null;
}

function Row({
  time,
  muted = false,
  children,
}: {
  time: string;
  muted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li
      className={cn(
        'flex items-start gap-3 py-2 px-2 rounded-md hover:bg-zinc-900/40 transition-colors border-b border-zinc-900/60',
        muted && 'opacity-60',
      )}
    >
      <span className="text-[11px] text-zinc-400 w-28 shrink-0 pt-0.5">{time}</span>
      {children}
    </li>
  );
}
